import { serializeQuery } from "@/utils/UrlUtils";
import { apiFetch, ApiBaseUrl } from "./fetch";

export default class ApiApps {
  static getApps(companyId) {
    let query = "";
    if (companyId && companyId.length) {
      query = `?company_id=${companyId}`;
    }
    return apiFetch(`${ApiBaseUrl}apps${query}`);
  }

  static getApp(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}`);
  }

  static createApp(modelApp) {
    return apiFetch(`${ApiBaseUrl}apps`, {
      method: "POST",
      body: { app: modelApp },
    });
  }

  static updateApp(app) {
    return apiFetch(`${ApiBaseUrl}apps/${app.id}`, {
      method: "PUT",
      body: { app },
    });
  }

  static deleteApp(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}`, {
      method: "DELETE",
    });
  }

  static updateAwsMarketplace(appId, clicks) {
    return apiFetch(`${ApiBaseUrl}aws_marketplace/${appId}`, {
      method: "PUT",
      body: { clicks },
    });
  }

  static imagewizUpdateCfDistro(appId, all) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/imagewiz_update_cf_distro`, {
      method: "POST",
      body: { all },
    });
  }

  static syncSfmc(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/sfmc_sync`, {
      method: "POST",
    });
  }

  static purgeSfmc(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/sfmc_purge`, {
      method: "POST",
    });
  }

  static publishSdk(appId, sdk_type, source_version) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/publish_sdk`, {
      method: "POST",
      body: { sdk_type, source_version },
    });
  }

  static generateApiKey(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/generate_api_key`, {
      method: "POST",
    });
  }

  static getActivities(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/activity`);
  }

  static getAwsCredentials(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/aws_creds`);
  }

  static getAppModel() {
    return apiFetch(`${ApiBaseUrl}apps/new`);
  }

  static getAppTemplates(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/templates`);
  }

  static getAppSequenceTemplates(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/templates/sequences`);
  }

  static searchEndUsers(appId, query) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/end_users?${query}`);
  }

  static deleteEndUser(appId, endUserId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/end_users/${endUserId}`, {
      method: "DELETE",
    });
  }

  static createEndUser(appId, traits) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/end_users`, {
      method: "POST",
      body: {
        end_user: traits,
      },
    });
  }

  static showEndUser(appId, userId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/end_users/${userId}/show_user`);
  }

  static async getCustomMetricsStatus(appId, params) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;

    if (appId) {
      if (
        params &&
        params.metric_periods &&
        params.metric_periods.find(item => item.period === "custom")
      ) {
        let metricsStatus = {};
        const startTime = +new Date();
        let currTime = 0;
        const timeoutSeconds = 180000;
        while (true) {
          metricsStatus = await apiFetch(
            `${ApiBaseUrl}apps/${appId}/custom_metrics_status${query}`,
          );
          currTime = +new Date();

          if (metricsStatus.status === "PROCESSED" || currTime - startTime > timeoutSeconds) {
            break;
          }
          await new Promise(resolve => {
            setTimeout(resolve, 3000);
          });
        }
      }
    }
  }

  static invalidateCache(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/invalidate_cache`, {
      method: "DELETE",
    });
  }

  static refreshTraitValues(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/refresh_trait_values`, {
      method: "POST",
    });
  }

  static newConnectorAction(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/connector_actions/new`);
  }

  static createPinpointApp(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/create_pinpoint_app`, {
      method: "POST",
    });
  }

  static createSampleData(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/create_sample_data`, {
      method: "POST",
    });
  }

  static createDefaultSequences(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/create_default_sequences`, {
      method: "POST",
    });
  }

  static verifyPinpointEmail(appId, email) {
    const query = serializeQuery({ email });
    return apiFetch(`${ApiBaseUrl}apps/${appId}/verify_pinpoint_email?${query}`, {
      method: "POST",
    });
  }

  static enablePinpointEmails(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/enable_pinpoint_emails`, {
      method: "POST",
    });
  }

  static connectors(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/connectors`);
  }

  static generateJwtSecret(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/generate_jwt_secret`, {
      method: "POST",
    });
  }

  static sdkActive(appId, reset = null) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/sdk`, {
      method: "POST",
      body: reset ? { reset } : null,
    });
  }

  static enablePushChannel(appId, channel) {
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/enable_push_channel?${serializeQuery({ channel })}`,
      { method: "POST" },
    );
  }

  static testPushChannel(appId, channel, tokens) {
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/test_push_channel?${serializeQuery({ channel, tokens })}`,
      { method: "POST" },
    );
  }

  static getLiveData(appId, params) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;

    return apiFetch(`${ApiBaseUrl}apps/${appId}/live_feed${query}`);
  }

  static getAppNotifications(appId) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/app_notifications`);
  }

  static updateAppNotifications(appId, appNotifications) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/app_notifications`, {
      method: "PUT",
      body: appNotifications,
    });
  }

  static sendAppNotification(appId, reportType) {
    return apiFetch(`${ApiBaseUrl}apps/${appId}/app_notifications/send_now/${reportType}`, {
      method: "POST",
    });
  }
}
